import { useEffect, useRef, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Mail01 } from '@untitledui/icons';
import { Input } from '@/components/base/input/input';
import { apiRequest } from '@/shared/api/client';
import { ENDPOINTS } from '@/shared/api/endpoints';
import { AUTH_FORM_SIZE } from '@/shared/config/auth-form';
import type { SignupFormValues } from '../schema';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const DEBOUNCE_MS = 500;

type CheckStatus = 'idle' | 'checking' | 'available' | 'taken' | 'error';

export const Step02Email = () => {
  const {
    control,
    setError,
    clearErrors,
    formState: { errors },
  } = useFormContext<SignupFormValues>();
  const [status, setStatus] = useState<CheckStatus>('idle');
  const timerRef = useRef<number | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const lastCheckedRef = useRef<string>('');

  useEffect(() => () => {
    if (timerRef.current) window.clearTimeout(timerRef.current);
    abortRef.current?.abort();
  }, []);

  const scheduleCheck = (raw: string) => {
    const value = raw.trim().toLowerCase();
    if (timerRef.current) window.clearTimeout(timerRef.current);
    abortRef.current?.abort();

    if (!value || !EMAIL_REGEX.test(value)) {
      setStatus('idle');
      lastCheckedRef.current = '';
      clearErrors('email');
      return;
    }
    if (value === lastCheckedRef.current) {
      return;
    }
    setStatus('checking');
    // Seta erro tipo 'checking' (sem mensagem) para bloquear o botao
    // "Continuar" enquanto a verificacao assincrona nao termina. O
    // SignupFlow.stepIsValid considera qualquer erro no formState como
    // bloqueio; com message='' o Input nao mostra texto de erro -- o
    // hint "Verificando..." abaixo cuida do feedback visual.
    setError('email', { type: 'checking', message: '' });
    timerRef.current = window.setTimeout(async () => {
      const controller = new AbortController();
      abortRef.current = controller;
      try {
        const data = await apiRequest<{ available: boolean }>({
          method: 'POST',
          url: ENDPOINTS.AUTH.EMAIL_AVAILABILITY,
          data: { email: value },
          signal: controller.signal,
        });
        lastCheckedRef.current = value;
        if (data.available) {
          setStatus('available');
          clearErrors('email');
        } else {
          setStatus('taken');
          setError('email', {
            type: 'manual',
            message: 'Este e-mail já está cadastrado. Use outro ou faça login.',
          });
        }
      } catch (err) {
        const error = err as { name?: string };
        if (error.name === 'CanceledError' || error.name === 'AbortError') return;
        // Em caso de erro, nao bloqueia o avanco -- o POST /register vai
        // validar definitivamente. Apenas mostra que o check nao concluiu.
        setStatus('error');
        clearErrors('email');
      }
    }, DEBOUNCE_MS);
  };

  const hint = (() => {
    if (errors.email?.message) return errors.email.message;
    if (status === 'checking') return 'Verificando...';
    if (status === 'available') return 'E-mail disponível.';
    if (status === 'error') return 'Não foi possível verificar agora. Você pode continuar.';
    return undefined;
  })();

  return (
    <Controller
      control={control}
      name="email"
      render={({ field }) => (
        <Input
          label="Email"
          type="email"
          size={AUTH_FORM_SIZE}
          icon={Mail01}
          placeholder="voce@empresa.com"
          autoComplete="email"
          autoFocus
          inputMode="email"
          // Nao marca como invalido enquanto checking (sem mensagem) --
          // evita borda vermelha durante a espera; "Verificando..." aparece
          // como hint neutro.
          isInvalid={(!!errors.email && errors.email.type !== 'checking') || status === 'taken'}
          hint={hint}
          value={field.value ?? ''}
          onChange={(value: string) => {
            field.onChange(value);
            scheduleCheck(value);
          }}
          onBlur={field.onBlur}
          name={field.name}
        />
      )}
    />
  );
};
